import {
  GKButton,
  GKDatePicker,
  GKModal,
  GKTextArea,
  GKTextField,
  Grid2,
  NumberIcon,
  Typography,
} from "components";
import dayjs from "dayjs";
import { Controller, useForm } from "react-hook-form";
import { useProformaConvertToInvoiceMutation } from "store/features/Revenue/ProformaApi";
import { TAB } from "store/slices/appSlice";
import { useAppDispatch } from "store/store";
import { numberToCurrency } from "utils";
import { GKModalProps } from "utils/commonInterface";
import { matchDate } from "utils/helpers";
import { validateNotWhitespaceOnly } from "utils/validationSchema";

interface Props {
  proformaDetails: {
    id: string;
    date: string;
    proformaNumber: string;
    proformaPdf: string;
    feePlanName: string;
    remainingPaidAmount: number;
    totalFee: number;
    isInvoice: boolean;
    isForfeit: boolean;
    client: number;
    portfolio: number;
    clientName: string;
    clientCode: string;
    portfolioName: string;
    portfolioId: string;
  };
}

const ProformaToInvoiceModal = (props: GKModalProps & Props) => {
  const { open, setOpen, proformaDetails } = props;

  const dispatch = useAppDispatch();

  const form = useForm({
    defaultValues: {
      invoiceDate: "",
      invoiceNumber: "",
      remarks: "Thanks for your business.",
    },
  });

  const [proformaConvertToInvoice, { isLoading }] =
    useProformaConvertToInvoiceMutation();

  return (
    <GKModal
      open={open}
      setOpen={setOpen}
      modalTitle="Convert to Invoice"
      width={"570px"}
      footer={
        <GKButton
          type="submit"
          variant="contained"
          form="proforma-convert-invoice-form"
          loading={isLoading}
        >
          Save
        </GKButton>
      }
    >
      <form
        id="proforma-convert-invoice-form"
        onSubmit={form.handleSubmit(async (data) => {
          try {
            await proformaConvertToInvoice({
              id: proformaDetails?.id,
              data,
            }).unwrap();
            setOpen(false);
            dispatch(TAB({ tabKey: "tabValueOfRevenue", tabValue: 5 }));
          } catch (err) {
            console.error("Proforma to invoice convert failed:", err);
          }
        })}
      >
        <Grid2 container spacing={3} mt={1}>
          <Grid2
            container
            size={12}
            p={2}
            borderRadius={2}
            bgcolor={"var(--grey-bg-100)"}
            spacing={1}
          >
            <Grid2
              size={12}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Typography fontWeight={500} color="var(--black)">
                {proformaDetails?.clientName}
              </Typography>
              <Typography fontSize={18} fontWeight={500} color="var(--black)">
                {numberToCurrency(proformaDetails?.totalFee)}
              </Typography>
            </Grid2>
            {[
              {
                label: "Portfolio Name / Code:",
                value: `${proformaDetails?.portfolioName} - ${proformaDetails?.portfolioId}`,
              },
              {
                label: "Proforma Invoice No:",
                value: proformaDetails?.proformaNumber,
              },
              {
                label: "Creation Date:",
                value: matchDate(proformaDetails?.date),
              },
              // {
              //   label: "Due Date:",
              //   value: matchDate(proformaDetails?.date),
              // },
            ].map((item, index) => (
              <Grid2
                container
                size={12}
                spacing={1}
                key={index}
                mt={index === 0 ? 0.5 : undefined}
              >
                <Grid2 size={3.5}>
                  <Typography
                    fontSize={12}
                    fontWeight={500}
                    color="var(--neutral-500)"
                    textAlign="start"
                  >
                    {item.label}
                  </Typography>
                </Grid2>
                <Grid2 size={7.5}>
                  <Typography
                    fontSize={12}
                    fontWeight={500}
                    color="var(--neutral-700)"
                  >
                    {item.value}
                  </Typography>
                </Grid2>
              </Grid2>
            ))}
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="invoiceDate"
              control={form.control}
              render={({ field }) => (
                <GKDatePicker
                  {...field}
                  inputLabel="Invoice Date"
                  value={dayjs(field.value)}
                  onChange={(value) => {
                    field.onChange(dayjs(value).format("YYYY-MM-DD"));
                  }}
                  disableFuture
                  slotProps={{
                    textField: {
                      error: false,
                      helperText: false,
                    },
                  }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="invoiceNumber"
              control={form.control}
              rules={{
                required: "Invoice number is required",
                minLength: {
                  value: 1,
                  message: "Minimum length is 1 characters",
                },
                maxLength: {
                  value: 49,
                  message: "Maximum length is 49 characters",
                },
                validate: validateNotWhitespaceOnly,
              }}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  inputLabel="Invoice Number"
                  error={!!error}
                  helperText={!!error?.message}
                  slotProps={{ input: { endAdornment: <NumberIcon /> } }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={12}>
            <Controller
              name="remarks"
              control={form.control}
              render={({ field }) => (
                <GKTextArea {...field} inputLabel="Remarks" />
              )}
            />
          </Grid2>
        </Grid2>
      </form>
    </GKModal>
  );
};

export default ProformaToInvoiceModal;
